import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Breadcrumb, Text } from '@/uicomponents';
import styles from './page-breadcrumb.module.css';

export interface PageBreadcrumbItem {
  /** Crumb label. */
  label: ReactNode;
  /**
   * Route this crumb navigates to. Omit on the current page's crumb — the last
   * crumb is never a link, and its full-strength colour is what says "you are here".
   */
  to?: string;
  /**
   * Truncate this crumb with an ellipsis, exposing the full value on hover (NPJB-636).
   *
   * Set it on crumbs carrying a **name**. The structural parts — `Campaigns`, the separators —
   * are deliberately left alone, so the trail's shape stays readable however long the names get
   * and a name crumb is always the one that absorbs the truncation. Two truncatable crumbs side
   * by side shrink in proportion to their own length, so a short campaign name keeps its full
   * text beside a long audience name.
   */
  truncate?: boolean;
}

interface PageBreadcrumbProps {
  /** Ancestors first, current page last. */
  items: PageBreadcrumbItem[];
}

/**
 * The page breadcrumb trail — the single implementation for every in-app breadcrumb.
 *
 * Built on antd `Breadcrumb` rather than a hand-rolled `Flex` + `Link` row for two
 * reasons: it emits a real `<nav>` landmark (a hand-rolled row is an unlabelled `div`
 * to a screen reader), and its colours come from the `Breadcrumb` theme tokens, so the
 * trail cannot drift per page the way four separate copies did.
 *
 * Colour rules live in the theme (`src/config/theme.ts`) and the rule catalog's
 * "N — Breadcrumbs" section: ancestors Link Blue, separators border-dim, current crumb
 * body. Nothing here should re-specify a colour.
 */
const renderCrumb = ({ label, to, truncate }: PageBreadcrumbItem): ReactNode => {
  // `ellipsis={{ tooltip }}` is the repo's established truncation idiom, and antd shows the
  // tooltip only when the text is actually clipped — so an untruncated crumb gains no hover.
  const ellipsis = truncate ? { tooltip: label } : undefined;

  // Current page: plain text, announced as the current location. No `variant` or `tone` —
  // colour comes from the Breadcrumb theme (`lastItemColor`). The size has to be *inherited*
  // explicitly: `Text` ships antd's 14px body size, which outranks the theme's `fontSize: 12`
  // and rendered this crumb two px larger than the links beside it.
  if (!to)
    return (
      <Text aria-current='page' className={styles.currentCrumb} ellipsis={ellipsis}>
        {label}
      </Text>
    );
  // A link's label stays a bare node unless it truncates: clipping needs a `Text`, and that
  // `Text` inherits colour from the `<a>` so the theme's link tokens still own the colour.
  if (!truncate) return <Link to={to}>{label}</Link>;
  return (
    <Link to={to}>
      <Text className={styles.crumbText} ellipsis={ellipsis}>
        {label}
      </Text>
    </Link>
  );
};

export const PageBreadcrumb = ({ items }: PageBreadcrumbProps) => {
  // Built outside the JSX: this maps to antd's `items` *data* prop, not a rendered
  // list, so `<For>` (the usual list-rendering rule) doesn't apply. antd puts a per-item
  // `className` on the inner `<span class="ant-breadcrumb-link">`; the CSS module reaches
  // the surrounding `<li>` from there.
  const crumbs = items.map((item) => ({
    title: renderCrumb(item),
    className: item.truncate ? styles.truncate : undefined,
  }));
  return <Breadcrumb aria-label='Breadcrumb' className={styles.trail} items={crumbs} />;
};
